import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CLINIC } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.data.name}`);
      if (res.data.must_change_password) {
        nav("/force-change-password", { replace: true });
      } else if (res.data.role === "patient") {
        nav("/portal");
      } else {
        nav("/dashboard");
      }
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--bg-marketing)]">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden brand-gradient text-white">
        <Link to="/" className="flex items-center gap-3 relative z-10" data-testid="login-logo-link">
          <img src={CLINIC.logoUrl} alt={CLINIC.name} className="w-12 h-12 rounded-full bg-white p-0.5" />
          <span className="font-heading text-[15px] font-medium leading-tight">{CLINIC.shortName}<br /><span className="text-[10px] tracking-[0.18em] uppercase opacity-80 font-normal">Homoeopathy Clinic</span></span>
        </Link>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-5xl font-light leading-tight">{CLINIC.tagline.split(" ").slice(0,2).join(" ")},<br /><span className="italic opacity-90">{CLINIC.tagline.split(" ").slice(2).join(" ")}</span></h1>
          <p className="mt-5 text-white/85 leading-relaxed">Sign in to manage patients, appointments and prescriptions — or access your patient portal.</p>
        </div>
        <p className="text-xs text-white/70 relative z-10">© {CLINIC.name}</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm" data-testid="login-form">
          <h2 className="font-heading text-3xl font-medium text-[color:var(--ink)] mb-1">Welcome back</h2>
          <p className="text-sm text-[color:var(--muted)] mb-7">Sign in to continue.</p>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Email</label>
              <Input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-2" data-testid="login-email" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Password</label>
              <Input required type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-2" data-testid="login-password" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-11" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <p className="mt-4 text-sm text-center">
            <Link to="/forgot-password" className="text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-testid="login-forgot-link">
              Forgot your password?
            </Link>
          </p>

          <p className="mt-3 text-sm text-[color:var(--muted)] text-center">
            New here? <Link to="/register" className="text-[color:var(--secondary)] hover:underline font-medium" data-testid="login-register-link">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
